import React, { useEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Lightbulb, X } from 'lucide-react';
import { useFeatureDiscovery } from '../context/FeatureDiscoveryContext';

/**
 * FeatureHint — one-time contextual tooltip for onboarding.
 *
 * Wraps any element with a floating bubble that appears the first time
 * the user reaches it, then never again (persisted via FeatureDiscoveryContext).
 *
 * Usage:
 *   <FeatureHint
 *     id="profile-name-field"
 *     title="Ton prénom"
 *     body="Rentre ton prénom ici pour personnaliser ton expérience."
 *     side="right"
 *   >
 *     <Input ... />
 *   </FeatureHint>
 *
 * Props:
 *   - id       : unique string (persisted in localStorage)
 *   - title    : short bold header (string)
 *   - body     : explanation (string or JSX)
 *   - side     : popover side ('top' | 'right' | 'bottom' | 'left'), default 'bottom'
 *   - delay    : ms before showing (default 600 — lets the page settle)
 *   - dismissOnInteract : auto-dismiss as soon as user interacts with the child (default true)
 */
const FeatureHint = ({
  id,
  title,
  body,
  side = 'bottom',
  align = 'start',
  delay = 600,
  dismissOnInteract = true,
  children,
}) => {
  const { hasSeen, markSeen } = useFeatureDiscovery();
  const alreadySeen = hasSeen(id);
  const [open, setOpen] = useState(false);
  const openedOnceRef = useRef(false);

  useEffect(() => {
    if (alreadySeen || openedOnceRef.current) return;
    const t = setTimeout(() => {
      openedOnceRef.current = true;
      setOpen(true);
    }, delay);
    return () => clearTimeout(t);
  }, [alreadySeen, delay]);

  const dismiss = () => {
    setOpen(false);
    markSeen(id);
  };

  // If the user already saw the hint, just pass the child through unchanged.
  if (alreadySeen) return <>{children}</>;

  // Intercept interactions (onFocus/onClick/onChange) to auto-dismiss.
  const child = React.Children.only(children);
  const augmented = dismissOnInteract
    ? React.cloneElement(child, {
        onFocus: (e) => { dismiss(); child.props.onFocus && child.props.onFocus(e); },
        onClick: (e) => { dismiss(); child.props.onClick && child.props.onClick(e); },
        onChange: (e) => { dismiss(); child.props.onChange && child.props.onChange(e); },
      })
    : child;

  return (
    <Popover open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <PopoverTrigger asChild>{augmented}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-64 p-0 border-primary/50 bg-card shadow-lg shadow-primary/20"
        data-testid={`feature-hint-${id}`}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-3 space-y-2">
          <div className="flex items-start gap-2">
            <div className="mt-0.5 shrink-0 text-primary">
              <Lightbulb className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading uppercase text-[11px] tracking-wider text-primary">
                {title}
              </p>
              <p className="text-xs text-foreground mt-1 leading-relaxed">{body}</p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="shrink-0 text-muted-foreground hover:text-foreground -mt-0.5 -mr-0.5"
              aria-label="Fermer"
              data-testid={`feature-hint-dismiss-${id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={dismiss}
              className="h-7 text-[11px] uppercase tracking-wider text-primary hover:bg-primary/10"
              data-testid={`feature-hint-ok-${id}`}
            >
              Compris
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FeatureHint;
